import React, { useEffect, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useGetKRDetailsQuery } from 'components/RabbitOKRSidePanel/datahooks';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useProfilePage } from 'screens/Profile/hooks/dataHooks';
import useRouteQueryParams from 'screens/performanceReview/hooks/useRouteQueryParams';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import { modifyString, StringModifier } from 'constants/copyHelpers';
import { getQueryParams } from 'constants/helper';
import { sendOpenedRequestFeedbackEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import Layout from '../common/Layout';
import {
  useRequestedFeedbackData,
  setRequestedSecondaryPanelOpen,
  setClearRequestedFeedbackData,
  setRequestedGoal,
  setRequestedProviderEmployees,
  setRequestedQuestion,
} from '../slice/feedbackSlice';
import { RequestFeedbackBody } from './RequestFeedbackBody';
import { useSaveRequestFeedback } from './dataHooks';
import { useSaveButtonDisabled } from './hooks';

interface Props {
  handleClose: () => void;
}

const RequestFeedback = ({ handleClose: handleCloseDrawer }: Props) => {
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);
  const history = useHistory();
  const queryClient = useQueryClient();

  const { moduleName } = useOrganisationSettings();
  const { goal, receiverEmployeeId, isSecondaryPanelOpen, providerEmployees, questionText } =
    useRequestedFeedbackData();

  const { employeeId: employeeIdFromPath } = useRouteQueryParams(routes.profile);

  const {
    source,
    empId: feedbackProviderIdFromQuery,
    feedbackReceiverId,
    requestText,
    goalId,
  } = getQueryParams(history.location.search);

  const feedbackProviderId = employeeIdFromPath ?? feedbackProviderIdFromQuery;

  const { data: providerData } = useProfilePage({ employeeId: feedbackProviderId });

  const relatedGoalDetails = useGetKRDetailsQuery({ id: goalId, enabled: !!goalId });

  const dispatch = useDispatch();

  const prefilledProviderEmployee = useMemo(() => {
    if (providerData) {
      return {
        id: providerData?.id,
        displayName: providerData?.displayName,
        profilePhoto: providerData?.profilePhotoSrc,
        email: providerData?.email,
        isSelected: true,
      };
    }
    return null;
  }, [providerData]);

  const setProviderEmployees = (employees) => {
    dispatch(setRequestedProviderEmployees({ providerEmployees: employees }));
  };

  useEffect(() => {
    if (prefilledProviderEmployee && prefilledProviderEmployee.id !== loggedInEmployeeId) {
      setProviderEmployees([prefilledProviderEmployee]);
    }
  }, [prefilledProviderEmployee, feedbackProviderId, loggedInEmployeeId]);

  useEffect(() => {
    sendOpenedRequestFeedbackEvent({
      eventProperties: {
        source,
      },
    });
  }, [source]);

  useEffect(() => {
    if (requestText) {
      dispatch(
        setRequestedQuestion({ questionText: Array.isArray(requestText) ? requestText.join(',') : requestText })
      );
    }
  }, [dispatch, requestText]);

  useEffect(() => {
    if (relatedGoalDetails?.data) {
      dispatch(
        setRequestedGoal({
          goal: { ...relatedGoalDetails?.data, title: relatedGoalDetails?.data?.text },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, goalId, relatedGoalDetails?.data]);

  const prefilledReceiverId = feedbackReceiverId ?? loggedInEmployeeId;

  const openSecondaryPanel = () => {
    dispatch(
      setRequestedSecondaryPanelOpen({
        isSecondaryPanelOpen: true,
      })
    );
  };

  const closeSecondaryPanel = () => {
    dispatch(
      setRequestedSecondaryPanelOpen({
        isSecondaryPanelOpen: false,
      })
    );
  };

  // eslint-disable-next-line no-shadow
  const onGoalSelect = (goal) => {
    dispatch(
      setRequestedGoal({
        goal,
      })
    );
  };

  const onClose = () => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.meshRecommends.nudgeCards]);
    handleCloseDrawer();
    setTimeout(() => {
      dispatch(setClearRequestedFeedbackData());
    }, 500);
  };

  const { onSave, isSaving } = useSaveRequestFeedback({ source: source as string, onClose });

  const saveButtonDisabled = useSaveButtonDisabled({
    receiverEmployeeId,
    providerEmployees: providerEmployees.length,
    isSaving,
    questionText,
  });

  return (
    <Layout
      header="Request Feedback"
      onClose={onClose}
      onSave={onSave}
      showSecondaryPanel={isSecondaryPanelOpen}
      // @ts-ignore
      selectedId={goal?.uuid}
      onSelect={onGoalSelect}
      secondaryPanelHeader={`Related ${modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)}`}
      onSecondaryPanelClose={closeSecondaryPanel}
      applicableEmployees={receiverEmployeeId ? [receiverEmployeeId] : []}
      saveButtonDisabled={saveButtonDisabled.isDisabled}
      saveButtonDisabledReason={saveButtonDisabled.reason}
    >
      <RequestFeedbackBody openSecondaryPanel={openSecondaryPanel} prefilledReceiverId={prefilledReceiverId} />
    </Layout>
  );
};

export default RequestFeedback;
